import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Users,
  Plus,
  UserCog,
  Shield,
  Trash2,
  Edit2,
  X,
  Check,
  Loader2,
  Bell,
  Mail,
  Send,
  CalendarDays,
  CalendarRange,
  Lock,
  Download,
  Archive,
  CheckCircle2,
  MapPin,
  Tag,
  Sparkles,
  UserCheck,
} from 'lucide-react';

export default function Settings() {
  const { user, authAxios } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingEmail, setEditingEmail] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', role: '', status: '' });
  const [deleteCandidate, setDeleteCandidate] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [locationsForPin, setLocationsForPin] = useState([]);
  const [pinDialogUser, setPinDialogUser] = useState(null); // user object

  // Notification preferences
  const [notifPrefs, setNotifPrefs] = useState({ individual_reminders: true, daily_digest: false, weekly_digest: false, monthly_contract: false });
  const [notifLoading, setNotifLoading] = useState(true);
  const [notifSaving, setNotifSaving] = useState(false);
  const [testSending, setTestSending] = useState(false);
  const [resendConfigured, setResendConfigured] = useState(false);

  const isOwner = user?.role === 'owner';

  // Load notification settings
  const loadNotifSettings = async () => {
    try {
      const api = authAxios();
      const res = await api.get('/notifications/settings');
      setNotifPrefs({
        individual_reminders: res.data.individual_reminders,
        daily_digest: res.data.daily_digest,
        weekly_digest: res.data.weekly_digest,
        monthly_contract: res.data.monthly_contract,
      });
      setResendConfigured(res.data.resend_configured);
    } catch (e) {
      console.error('Failed to load notification settings:', e);
    } finally {
      setNotifLoading(false);
    }
  };

  const updateNotifPref = async (key, value) => {
    setNotifSaving(true);
    const updated = { ...notifPrefs, [key]: value };
    setNotifPrefs(updated);
    try {
      const api = authAxios();
      await api.put('/notifications/settings', { [key]: value });
      toast.success('Voorkeur opgeslagen');
    } catch (e) {
      setNotifPrefs(notifPrefs); // revert
      toast.error('Fout bij opslaan');
    } finally {
      setNotifSaving(false);
    }
  };

  const sendTestEmail = async () => {
    setTestSending(true);
    try {
      const api = authAxios();
      await api.post('/notifications/test');
      toast.success('Test e-mail verstuurd!');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fout bij versturen test e-mail');
    } finally {
      setTestSending(false);
    }
  };

  const loadUsers = async () => {
    if (!isOwner) { setLoading(false); return; }
    try {
      const api = authAxios();
      const [usersRes, locsRes] = await Promise.all([
        api.get('/users'),
        api.get('/locations'),
      ]);
      setUsers(usersRes.data);
      setLocationsForPin((locsRes.data || []).filter(l => l.status === 'active'));
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
    loadNotifSettings();
  }, [isOwner]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleUpdateUser = async (email) => {
    try {
      const api = authAxios();
      const updates = {};
      if (editForm.name) updates.name = editForm.name;
      if (editForm.role) updates.role = editForm.role;
      if (editForm.status) updates.status = editForm.status;
      await api.put(`/users/${encodeURIComponent(email)}`, updates);
      toast.success('Gebruiker bijgewerkt');
      setEditingEmail(null);
      await loadUsers();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fout bij bijwerken');
    }
  };

  const saveUserPinnedLocations = async (email, pinned) => {
    try {
      const api = authAxios();
      await api.put(`/users/${encodeURIComponent(email)}`, { pinned_locations: pinned });
      toast.success('Vastgezette locaties bijgewerkt');
      setPinDialogUser(null);
      await loadUsers();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fout bij bijwerken');
    }
  };

  const handleDeleteUser = (email, name) => {
    setDeleteCandidate({ email, name });
  };

  const confirmDeleteUser = async () => {
    if (!deleteCandidate) return;
    setDeleting(true);
    try {
      const api = authAxios();
      await api.delete(`/users/${encodeURIComponent(deleteCandidate.email)}`);
      toast.success(`${deleteCandidate.name || deleteCandidate.email} verwijderd`);
      setDeleteCandidate(null);
      await loadUsers();
    } catch (e) {
      console.error('Delete user error:', e);
      toast.error(e.response?.data?.detail || 'Fout bij verwijderen');
    } finally {
      setDeleting(false);
    }
  };

  const startEdit = (u) => {
    setEditingEmail(u.email);
    setEditForm({ name: u.name, role: u.role, status: u.status || 'active' });
  };

  const ROLE_BADGE = {
    owner: 'bg-purple-100 text-purple-700 border-purple-200',
    admin: 'bg-blue-100 text-blue-700 border-blue-200',
  };

  return (
    <div className="max-w-[900px] space-y-6">
      {/* Account Info */}
      <div className="bg-white dark:bg-[#161B22] rounded-xl border border-slate-200/80 dark:border-[#21262D] p-6">
        <h3 className="font-heading text-sm font-semibold text-slate-900 dark:text-[#E6EDF3] mb-4 flex items-center gap-2">
          <UserCog className="w-4 h-4" />
          Account
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <span className="text-[10px] uppercase tracking-wider text-slate-400 block mb-1">Naam</span>
            <span className="text-sm text-slate-700 dark:text-[#C9D1D9]">{user?.name}</span>
          </div>
          <div>
            <span className="text-[10px] uppercase tracking-wider text-slate-400 block mb-1">Email</span>
            <span className="text-sm text-slate-700 dark:text-[#C9D1D9]">{user?.email}</span>
          </div>
          <div>
            <span className="text-[10px] uppercase tracking-wider text-slate-400 block mb-1">Rol</span>
            <span className={`inline-block text-xs px-2 py-0.5 rounded-full border capitalize ${ROLE_BADGE[user?.role] || ''}`}>
              {user?.role}
            </span>
          </div>
        </div>
      </div>

      {/* Password management lives in TW1 Identity Platform */}
      <IdentityPlatformNotice />

      {/* Notification Preferences */}
      <div className="bg-white rounded-xl border border-slate-200/80 p-6" data-testid="notification-settings">
        <h3 className="font-heading text-sm font-semibold text-slate-900 mb-4 flex items-center gap-2">
          <Bell className="w-4 h-4" />
          E-mailnotificaties
        </h3>

        {!resendConfigured && !notifLoading && (
          <div className="mb-4 p-3 rounded-lg bg-amber-50 border border-amber-200">
            <p className="text-xs text-amber-700">E-mail service is niet geconfigureerd. Notificaties worden pas verstuurd wanneer de RESEND_API_KEY is ingesteld.</p>
          </div>
        )}

        {notifLoading ? (
          <div className="animate-pulse space-y-4">
            {[1,2,3].map(i => <div key={i} className="h-12 bg-slate-100 rounded" />)}
          </div>
        ) : (
          <div className="space-y-3">
            {/* Individual Reminders */}
            <div className="flex items-center justify-between p-3 rounded-lg bg-slate-50 border border-slate-100">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-[#2E75B6]/10 flex items-center justify-center">
                  <Mail className="w-4 h-4 text-[#2E75B6]" />
                </div>
                <div>
                  <p className="text-sm font-medium text-slate-700">Individuele herinneringen</p>
                  <p className="text-[11px] text-slate-400">Ontvang e-mails wanneer compliance-items de deadline naderen</p>
                </div>
              </div>
              <button
                data-testid="toggle-individual-reminders"
                onClick={() => updateNotifPref('individual_reminders', !notifPrefs.individual_reminders)}
                disabled={notifSaving}
                className={`relative w-10 h-5 rounded-full transition-colors ${notifPrefs.individual_reminders ? 'bg-[#2E75B6]' : 'bg-slate-300'}`}
              >
                <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${notifPrefs.individual_reminders ? 'translate-x-5' : 'translate-x-0'}`} />
              </button>
            </div>

            {/* Daily Digest */}
            <div className="flex items-center justify-between p-3 rounded-lg bg-slate-50 border border-slate-100">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-[#2E75B6]/10 flex items-center justify-center">
                  <CalendarDays className="w-4 h-4 text-[#2E75B6]" />
                </div>
                <div>
                  <p className="text-sm font-medium text-slate-700">Dagelijkse samenvatting</p>
                  <p className="text-[11px] text-slate-400">Dagelijks overzicht van verlopen en binnenkort vervallende items (ma-vr)</p>
                </div>
              </div>
              <button
                data-testid="toggle-daily-digest"
                onClick={() => updateNotifPref('daily_digest', !notifPrefs.daily_digest)}
                disabled={notifSaving}
                className={`relative w-10 h-5 rounded-full transition-colors ${notifPrefs.daily_digest ? 'bg-[#2E75B6]' : 'bg-slate-300'}`}
              >
                <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${notifPrefs.daily_digest ? 'translate-x-5' : 'translate-x-0'}`} />
              </button>
            </div>

            {/* Weekly Digest */}
            <div className="flex items-center justify-between p-3 rounded-lg bg-slate-50 border border-slate-100">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-[#2E75B6]/10 flex items-center justify-center">
                  <CalendarRange className="w-4 h-4 text-[#2E75B6]" />
                </div>
                <div>
                  <p className="text-sm font-medium text-slate-700">Wekelijkse samenvatting</p>
                  <p className="text-[11px] text-slate-400">Wekelijks overzicht per locatie, elke maandag</p>
                </div>
              </div>
              <button
                data-testid="toggle-weekly-digest"
                onClick={() => updateNotifPref('weekly_digest', !notifPrefs.weekly_digest)}
                disabled={notifSaving}
                className={`relative w-10 h-5 rounded-full transition-colors ${notifPrefs.weekly_digest ? 'bg-[#2E75B6]' : 'bg-slate-300'}`}
              >
                <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${notifPrefs.weekly_digest ? 'translate-x-5' : 'translate-x-0'}`} />
              </button>
            </div>

            {/* Monthly Contract Overview */}
            <div className="flex items-center justify-between p-3 rounded-lg bg-slate-50 border border-slate-100">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-[#2E75B6]/10 flex items-center justify-center">
                  <Lock className="w-4 h-4 text-[#2E75B6]" />
                </div>
                <div>
                  <p className="text-sm font-medium text-slate-700">Maandelijks contractoverzicht</p>
                  <p className="text-[11px] text-slate-400">Maandoverzicht van opzegmomenten, indexaties en aflopende contracten</p>
                </div>
              </div>
              <button
                data-testid="toggle-monthly-contract"
                onClick={() => updateNotifPref('monthly_contract', !notifPrefs.monthly_contract)}
                disabled={notifSaving}
                className={`relative w-10 h-5 rounded-full transition-colors ${notifPrefs.monthly_contract ? 'bg-[#2E75B6]' : 'bg-slate-300'}`}
              >
                <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${notifPrefs.monthly_contract ? 'translate-x-5' : 'translate-x-0'}`} />
              </button>
            </div>

            {/* Test Email Button */}
            <div className="pt-2 flex items-center justify-between">
              <p className="text-[11px] text-slate-400">Stuur een test e-mail om de configuratie te controleren</p>
              <button
                data-testid="send-test-email"
                onClick={sendTestEmail}
                disabled={testSending || !resendConfigured}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-slate-100 text-slate-600 text-xs font-medium hover:bg-slate-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {testSending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                Test e-mail versturen
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Data Export (Owner only) */}
      {isOwner && <ExportSection />}

      {/* AI Normalization Settings */}
      <AINormalizationSettings />

      {/* Auto-approve Settings (Owner only) */}
      {isOwner && <AutoApproveSettings />}

      {/* Default obligation owner (Owner only) */}
      {isOwner && <DefaultObligationOwnerSettings />}

      {/* Location Management (visible to all, editable by owner) */}
      <LocationManagement />

      {/* Category Management (Owner only) */}
      {isOwner && <CategoryManagement />}

      {/* User Management (Owner only) */}
      {isOwner && (
        <div className="bg-white rounded-xl border border-slate-200/80 p-6">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-heading text-sm font-semibold text-slate-900 flex items-center gap-2">
              <Users className="w-4 h-4" />
              Gebruikersbeheer
            </h3>
          </div>
          <p className="text-[11px] text-slate-500 bg-blue-50 border border-blue-200 rounded px-3 py-2 mb-4">
            Nieuwe gebruikers worden automatisch aangemaakt bij hun eerste login via <span className="font-medium">TW1 Identity</span>.
            Hier beheer je alleen de Vault-specifieke instellingen: rol, status en vastgezette locaties.
          </p>

          {/* Users list */}
          {loading ? (
            <div className="animate-pulse space-y-3">
              {[1,2,3].map(i => <div key={i} className="h-12 bg-slate-100 dark:bg-[#21262D] rounded" />)}
            </div>
          ) : (
            <div data-testid="users-table">
              {/* Desktop: table, Mobile: cards */}
              <div className="hidden sm:block overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-100 dark:border-[#21262D]">
                      <th className="text-left text-[10px] uppercase tracking-wider text-slate-400 font-medium pb-2">Naam</th>
                      <th className="text-left text-[10px] uppercase tracking-wider text-slate-400 font-medium pb-2">Email</th>
                      <th className="text-left text-[10px] uppercase tracking-wider text-slate-400 font-medium pb-2">Rol</th>
                      <th className="text-left text-[10px] uppercase tracking-wider text-slate-400 font-medium pb-2">Status</th>
                      <th className="text-right text-[10px] uppercase tracking-wider text-slate-400 font-medium pb-2">Acties</th>
                    </tr>
                  </thead>
                  <tbody>
                  {users.map(u => (
                    <tr key={u.email} className="border-b border-slate-50 hover:bg-slate-50 transition-colors">
                      {editingEmail === u.email ? (
                        <>
                          <td className="py-2 pr-2">
                            <input
                              type="text"
                              value={editForm.name}
                              onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                              className="px-2 py-1 rounded border border-slate-200 bg-white text-slate-900 text-xs w-full"
                            />
                          </td>
                          <td className="py-2 pr-2 text-xs text-slate-500">{u.email}</td>
                          <td className="py-2 pr-2">
                            <select
                              value={editForm.role}
                              onChange={(e) => setEditForm({ ...editForm, role: e.target.value })}
                              className="px-2 py-1 rounded border border-slate-200 bg-white text-slate-900 text-xs"
                            >
                              <option value="admin">Admin</option>
                              <option value="owner">Owner</option>
                            </select>
                          </td>
                          <td className="py-2 pr-2">
                            <select
                              value={editForm.status}
                              onChange={(e) => setEditForm({ ...editForm, status: e.target.value })}
                              className="px-2 py-1 rounded border border-slate-200 bg-white text-slate-900 text-xs"
                            >
                              <option value="active">Actief</option>
                              <option value="inactive">Inactief</option>
                            </select>
                          </td>
                          <td className="py-2 text-right">
                            <div className="flex items-center gap-1 justify-end">
                              <button
                                onClick={() => handleUpdateUser(u.email)}
                                className="p-1.5 rounded text-green-600 hover:bg-green-50 transition-colors"
                              >
                                <Check className="w-3.5 h-3.5" />
                              </button>
                              <button
                                onClick={() => setEditingEmail(null)}
                                className="p-1.5 rounded text-slate-400 hover:bg-slate-100 transition-colors"
                              >
                                <X className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          </td>
                        </>
                      ) : (
                        <>
                          <td className="py-2.5 text-xs font-medium text-slate-700">{u.name}</td>
                          <td className="py-2.5 text-xs text-slate-500">{u.email}</td>
                          <td className="py-2.5">
                            <span className={`text-[10px] px-2 py-0.5 rounded-full border capitalize ${ROLE_BADGE[u.role] || ''}`}>
                              {u.role}
                            </span>
                          </td>
                          <td className="py-2.5">
                            <span className={`text-[10px] px-2 py-0.5 rounded-full ${u.status === 'active' ? 'badge-active' : 'badge-inactive'}`}>
                              {u.status === 'active' ? 'Actief' : 'Inactief'}
                            </span>
                          </td>
                          <td className="py-2.5 text-right">
                            <div className="flex items-center gap-1 justify-end">
                              <button
                                onClick={() => setPinDialogUser(u)}
                                data-testid={`pin-locations-${u.email}`}
                                className="p-1.5 rounded text-slate-400 hover:text-[#2E75B6] hover:bg-[#2E75B6]/5 transition-colors"
                                title={`Vastgezette locaties (${(u.pinned_locations || []).length})`}
                              >
                                <MapPin className="w-3.5 h-3.5" />
                              </button>
                              <button
                                onClick={() => startEdit(u)}
                                className="p-1.5 rounded text-slate-400 hover:text-[#2E75B6] hover:bg-[#2E75B6]/5 transition-colors"
                                title="Bewerken"
                              >
                                <Edit2 className="w-3.5 h-3.5" />
                              </button>
                              {u.email !== user?.email && u.role !== 'owner' && (
                                <button
                                  data-testid={`delete-user-${u.email}`}
                                  onClick={() => handleDeleteUser(u.email, u.name)}
                                  className="p-1.5 rounded text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                                  title="Verwijderen"
                                >
                                  <Trash2 className="w-3.5 h-3.5" />
                                </button>
                              )}
                            </div>
                          </td>
                        </>
                      )}
                    </tr>
                  ))}
                </tbody>
                </table>
              </div>

              {/* Mobile: card-based users */}
              <div className="sm:hidden space-y-2">
                {users.map(u => (
                  <div key={u.email} className="p-3 rounded-lg border border-slate-200 dark:border-[#21262D] bg-slate-50 dark:bg-[#0D1117]">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <UserCog className="w-4 h-4 text-slate-400" />
                        <span className="text-sm font-medium text-slate-900 dark:text-[#E6EDF3]">{u.name}</span>
                      </div>
                      <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${u.status === 'active' ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-slate-100 text-slate-500 border border-slate-200'}`}>
                        {u.status === 'active' ? 'Actief' : 'Inactief'}
                      </span>
                    </div>
                    <p className="text-xs text-slate-500 dark:text-[#8B949E] mb-1">{u.email}</p>
                    <div className="flex items-center justify-between mt-2">
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#2E75B6]/10 text-[#2E75B6] font-medium capitalize">{u.role}</span>
                        <span className="text-[10px] text-slate-400 flex items-center gap-1">
                          <MapPin className="w-3 h-3" />
                          {(u.pinned_locations || []).length}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => setPinDialogUser(u)}
                          className="p-2 rounded-lg text-slate-400 hover:text-[#2E75B6] hover:bg-[#2E75B6]/5"
                          title="Vastgezette locaties"
                        >
                          <MapPin className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => { setEditingEmail(u.email); setEditForm({ name: u.name, role: u.role, status: u.status }); }}
                          className="p-2 rounded-lg text-slate-400 hover:text-[#2E75B6] hover:bg-[#2E75B6]/5"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        {u.email !== user?.email && u.role !== 'owner' && (
                          <button
                            onClick={() => handleDeleteUser(u.email, u.name)}
                            className="p-2 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteCandidate} onOpenChange={(open) => { if (!open) setDeleteCandidate(null); }}>
        <AlertDialogContent data-testid="delete-user-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">Gebruiker verwijderen</AlertDialogTitle>
            <AlertDialogDescription>
              Weet je zeker dat je <span className="font-semibold text-slate-700">{deleteCandidate?.name}</span> ({deleteCandidate?.email}) wilt verwijderen? Dit kan niet ongedaan worden gemaakt.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-user-cancel" disabled={deleting}>Annuleren</AlertDialogCancel>
            <AlertDialogAction
              data-testid="delete-user-confirm"
              onClick={confirmDeleteUser}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {deleting && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
              Verwijderen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Pinned Locations Dialog */}
      <UserPinnedLocationsDialog
        user={pinDialogUser}
        locations={locationsForPin}
        onClose={() => setPinDialogUser(null)}
        onSave={saveUserPinnedLocations}
      />

      {!isOwner && (
        <div className="bg-white dark:bg-[#161B22] rounded-xl border border-slate-200/80 dark:border-[#21262D] p-6 text-center">
          <Shield className="w-10 h-10 text-slate-300 mx-auto mb-3" />
          <p className="text-sm text-slate-500 dark:text-[#8B949E]">Gebruikersbeheer is alleen beschikbaar voor eigenaren.</p>
        </div>
      )}
    </div>
  );
}

function ExportSection() {
  const { authAxios } = useAuth();
  const [exporting, setExporting] = useState(false);
  const [exportJob, setExportJob] = useState(null);
  const [polling, setPolling] = useState(false);

  const startFullExport = async () => {
    setExporting(true);
    try {
      const api = authAxios();
      const res = await api.post('/export/full');
      setExportJob({ job_id: res.data.job_id, status: 'processing' });
      pollStatus(res.data.job_id);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Export starten mislukt');
      setExporting(false);
    }
  };

  const pollStatus = async (jobId) => {
    setPolling(true);
    const api = authAxios();
    const check = async () => {
      try {
        const res = await api.get(`/export/status/${jobId}`);
        setExportJob(res.data);
        if (res.data.status === 'processing') {
          setTimeout(check, 2000);
        } else {
          setPolling(false);
          setExporting(false);
          if (res.data.status === 'done') {
            toast.success('Export is gereed!');
          } else {
            toast.error('Export mislukt: ' + (res.data.error || 'onbekende fout'));
          }
        }
      } catch (e) {
        setPolling(false);
        setExporting(false);
      }
    };
    check();
  };

  const downloadExport = () => {
    if (!exportJob?.job_id) return;
    const api = authAxios();
    api.get(`/export/download/${exportJob.job_id}`, { responseType: 'blob' })
      .then(res => {
        const blobUrl = URL.createObjectURL(res.data);
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = `tw1-vault-export-${exportJob.job_id}.zip`;
        a.click();
        URL.revokeObjectURL(blobUrl);
      })
      .catch(() => { toast.error('Download mislukt'); });
  };

  return (
    <div className="bg-white dark:bg-[#161B22] rounded-xl border border-slate-200/80 dark:border-[#21262D] p-6" data-testid="export-section">
      <h3 className="font-heading text-sm font-semibold text-slate-900 dark:text-[#E6EDF3] mb-3 flex items-center gap-2">
        <Archive className="w-4 h-4" />
        Data Export
      </h3>
      <p className="text-xs text-slate-500 dark:text-[#8B949E] mb-4">
        Exporteer alle locaties, kennisbank-items en bijlagen als ZIP-archief. Inclusief samenvattings-PDF's en compliance-CSV per locatie.
      </p>

      {exportJob?.status === 'done' ? (
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-green-50 dark:bg-green-500/10 border border-green-200 dark:border-green-500/30">
            <CheckCircle2 className="w-4 h-4 text-green-600" />
            <span className="text-xs text-green-700 dark:text-green-400 font-medium">
              Export gereed ({Math.round((exportJob.file_size || 0) / 1024)} KB)
            </span>
          </div>
          <button
            onClick={downloadExport}
            className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#2E75B6] text-white text-xs font-medium hover:bg-[#27649C] transition-colors"
            data-testid="export-download-button"
          >
            <Download className="w-3.5 h-3.5" /> Download ZIP
          </button>
          <button
            onClick={() => setExportJob(null)}
            className="text-xs text-slate-400 hover:text-slate-600 transition-colors"
          >
            Sluiten
          </button>
        </div>
      ) : (
        <button
          onClick={startFullExport}
          disabled={exporting}
          className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#2E75B6] text-white text-xs font-medium hover:bg-[#27649C] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid="export-full-button"
        >
          {exporting ? (
            <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Exporteren...</>
          ) : (
            <><Download className="w-3.5 h-3.5" /> Exporteer alles</>
          )}
        </button>
      )}
    </div>
  );
}

function UserPinnedLocationsDialog({ user, locations, onClose, onSave }) {
  const [selected, setSelected] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user) setSelected(user.pinned_locations || []);
  }, [user]);

  const toggle = (locationId) => {
    setSelected((prev) =>
      prev.includes(locationId) ? prev.filter((l) => l !== locationId) : [...prev, locationId]
    );
  };

  const handleSave = async () => {
    if (!user) return;
    setSaving(true);
    try {
      await onSave(user.email, selected);
    } finally {
      setSaving(false);
    }
  };

  // Group by bv_id for readability
  const byDomain = (locations || []).reduce((acc, loc) => {
    const k = loc.bv_id || 'overig';
    (acc[k] = acc[k] || []).push(loc);
    return acc;
  }, {});
  const domainKeys = Object.keys(byDomain).sort();

  return (
    <Dialog open={!!user} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="pin-locations-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-base">Vastgezette locaties</DialogTitle>
          <DialogDescription>
            {user?.name} ({user?.email}) — kies de locaties die deze gebruiker bovenaan in de sidebar ziet.
          </DialogDescription>
        </DialogHeader>
        <div className="max-h-[360px] overflow-y-auto pr-1 space-y-3">
          {domainKeys.map((k) => (
            <div key={k}>
              <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-1.5">{k}</p>
              <div className="space-y-1">
                {byDomain[k].map((loc) => (
                  <label
                    key={loc.location_id}
                    className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-slate-50 cursor-pointer"
                  >
                    <Checkbox
                      checked={selected.includes(loc.location_id)}
                      onCheckedChange={() => toggle(loc.location_id)}
                      data-testid={`pin-checkbox-${loc.location_id}`}
                    />
                    <span className="text-sm text-slate-700">{loc.name}</span>
                  </label>
                ))}
              </div>
            </div>
          ))}
          {!domainKeys.length && (
            <p className="text-xs text-slate-400 italic">Geen locaties gevonden.</p>
          )}
        </div>
        <DialogFooter>
          <button
            onClick={onClose}
            disabled={saving}
            className="px-3 py-1.5 rounded-lg border border-slate-200 text-xs text-slate-500 hover:bg-slate-100"
          >
            Annuleren
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            data-testid="pin-locations-save"
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#2E75B6] text-white text-xs font-medium hover:bg-[#27649C] disabled:opacity-50"
          >
            {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
            Opslaan ({selected.length})
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function IdentityPlatformNotice() {
  const IDP = process.env.REACT_APP_IDENTITY_PLATFORM_URL || 'https://id.truckwash1.cloud';
  return (
    <div
      className="bg-white dark:bg-[#161B22] rounded-xl border border-slate-200/80 dark:border-[#21262D] p-6"
      data-testid="identity-platform-notice"
    >
      <h3 className="font-heading text-sm font-semibold text-slate-900 dark:text-[#E6EDF3] mb-2 flex items-center gap-2">
        <Lock className="w-4 h-4" />
        Wachtwoord & account
      </h3>
      <p className="text-sm text-slate-600 dark:text-[#8B949E] mb-3">
        Je wachtwoord en 2FA worden beheerd in <span className="font-medium">TW1 Identity</span>.
        Hier kun je je wachtwoord wijzigen en andere accountinstellingen aanpassen.
      </p>
      <a
        href={IDP}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="open-identity-platform"
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#2E75B6] text-white text-xs font-medium hover:bg-[#27649C] transition-colors"
      >
        Open TW1 Identity
      </a>
    </div>
  );
}


function LocationManagement() {
  const { user, authAxios } = useAuth();
  const isOwner = user?.role === 'owner';
  const [locations, setLocations] = useState([]);
  const [domains, setDomains] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ location_id: '', name: '', status: 'active', bays: '', bv_id: 'truckwash' });
  const [error, setError] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({ name: '', status: '', bays: '' });
  const [editSaving, setEditSaving] = useState(false);

  const load = async () => {
    try {
      const api = authAxios();
      const [locRes, domRes] = await Promise.all([api.get('/locations'), api.get('/business-domains')]);
      setLocations(locRes.data);
      setDomains(domRes.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const STATUS_COLORS = {
    active: 'bg-green-50 text-green-700 border-green-200 dark:bg-green-500/10 dark:text-green-400 dark:border-green-500/30',
    planned: 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-500/10 dark:text-blue-400 dark:border-blue-500/30',
    inactive: 'bg-slate-100 text-slate-500 border-slate-200 dark:bg-slate-500/10 dark:text-slate-400 dark:border-slate-500/30',
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setError('');
    const lid = form.location_id.trim().toLowerCase();
    if (!lid || !form.name.trim()) { setError('Vul alle verplichte velden in'); return; }
    if (!/^[a-z0-9][a-z0-9-]*$/.test(lid)) { setError('Locatie ID: alleen kleine letters, cijfers en streepjes'); return; }
    setSaving(true);
    try {
      const api = authAxios();
      await api.post('/locations', { location_id: lid, name: form.name.trim(), status: form.status, bays: form.bays || '0', bv_id: form.bv_id });
      toast.success('Locatie aangemaakt');
      setShowForm(false);
      setForm({ location_id: '', name: '', status: 'active', bays: '', bv_id: 'truckwash' });
      await load();
    } catch (e) {
      setError(e.response?.data?.detail || 'Fout bij aanmaken');
    } finally { setSaving(false); }
  };

  const startEdit = (loc) => {
    setEditingId(loc.location_id);
    setEditData({ name: loc.name || '', status: loc.status || 'active', bays: String(loc.bays || '') });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditData({ name: '', status: '', bays: '' });
  };

  const saveEdit = async (locationId) => {
    setEditSaving(true);
    try {
      const api = authAxios();
      const payload = {};
      if (editData.name.trim()) payload.name = editData.name.trim();
      if (editData.status) payload.status = editData.status;
      if (editData.bays !== undefined) payload.bays = parseInt(editData.bays) || 0;
      await api.put(`/locations/${locationId}`, payload);
      toast.success('Locatie bijgewerkt');
      setEditingId(null);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fout bij opslaan');
    } finally { setEditSaving(false); }
  };

  const VALID_BVS = [
    { id: 'truckwash', label: 'Truckwash' },
    { id: 'truckshop', label: 'Truckshop' },
    { id: 'truckstop', label: 'Truckstop' },
    { id: 'truckssupply', label: 'Truckssupply' },
    { id: 'techniek', label: 'Techniek' },
    { id: 'holding', label: 'Holding' },
  ];

  return (
    <div className="bg-white dark:bg-[#161B22] rounded-xl border border-slate-200/80 dark:border-[#21262D] p-6" data-testid="location-management">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-heading text-sm font-semibold text-slate-900 dark:text-[#E6EDF3] flex items-center gap-2">
          <MapPin className="w-4 h-4" />
          Locaties beheren
        </h3>
        {isOwner && (
          <button
            data-testid="create-location-button"
            onClick={() => setShowForm(!showForm)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#2E75B6] text-white text-xs font-medium hover:bg-[#27649C] transition-colors"
          >
            <Plus className="w-3.5 h-3.5" />
            Nieuwe locatie
          </button>
        )}
      </div>

      {showForm && isOwner && (
        <form onSubmit={handleCreate} className="mb-4 p-4 rounded-lg bg-slate-50 dark:bg-[#0D1117] border border-slate-200 dark:border-[#30363D] space-y-3">
          {error && <p className="text-xs text-red-600 bg-red-50 dark:bg-red-500/10 px-3 py-2 rounded-lg">{error}</p>}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-wider text-slate-400 font-medium mb-1 block">Locatie ID *</label>
              <input
                type="text" value={form.location_id} data-testid="location-id-input"
                onChange={e => setForm({ ...form, location_id: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') })}
                placeholder="bijv. tilburg" className="w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-[#30363D] bg-white dark:bg-[#161B22] text-slate-900 dark:text-[#E6EDF3] text-sm focus:outline-none focus:ring-2 focus:ring-[#2E75B6]/40"
              />
              <p className="text-[10px] text-slate-400 mt-0.5">Permanent, kan niet gewijzigd worden</p>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-slate-400 font-medium mb-1 block">Naam *</label>
              <input
                type="text" value={form.name} data-testid="location-name-input"
                onChange={e => setForm({ ...form, name: e.target.value })}
                placeholder="bijv. Tilburg" className="w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-[#30363D] bg-white dark:bg-[#161B22] text-slate-900 dark:text-[#E6EDF3] text-sm focus:outline-none focus:ring-2 focus:ring-[#2E75B6]/40"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-slate-400 font-medium mb-1 block">BV / Domein</label>
              <select value={form.bv_id} onChange={e => setForm({ ...form, bv_id: e.target.value })} data-testid="location-bv-select"
                className="w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-[#30363D] bg-white dark:bg-[#161B22] text-slate-900 dark:text-[#E6EDF3] text-sm focus:outline-none focus:ring-2 focus:ring-[#2E75B6]/40">
                {VALID_BVS.map(b => <option key={b.id} value={b.id}>{b.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-slate-400 font-medium mb-1 block">Status</label>
              <select value={form.status} onChange={e => setForm({ ...form, status: e.target.value })} data-testid="location-status-select"
                className="w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-[#30363D] bg-white dark:bg-[#161B22] text-slate-900 dark:text-[#E6EDF3] text-sm focus:outline-none focus:ring-2 focus:ring-[#2E75B6]/40">
                <option value="active">Actief</option>
                <option value="planned">Gepland</option>
                <option value="inactive">Inactief</option>
              </select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-slate-400 font-medium mb-1 block">Aantal bays</label>
              <input
                type="text" value={form.bays} data-testid="location-bays-input"
                onChange={e => setForm({ ...form, bays: e.target.value })}
                placeholder="bijv. 2" className="w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-[#30363D] bg-white dark:bg-[#161B22] text-slate-900 dark:text-[#E6EDF3] text-sm focus:outline-none focus:ring-2 focus:ring-[#2E75B6]/40"
              />
            </div>
          </div>
          <div className="flex gap-2 justify-end">
            <button type="button" onClick={() => { setShowForm(false); setError(''); }} className="px-3 py-1.5 rounded-lg border border-slate-200 dark:border-[#30363D] text-xs text-slate-500 hover:bg-slate-100 dark:hover:bg-[#21262D] transition-colors">Annuleren</button>
            <button type="submit" disabled={saving} data-testid="create-location-submit"
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#2E75B6] text-white text-xs font-medium hover:bg-[#27649C] transition-colors disabled:opacity-50">
              {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />} Aanmaken
            </button>
          </div>
        </form>
      )}

      {loading ? (
        <div className="animate-pulse space-y-2">{[1,2,3].map(i => <div key={i} className="h-10 bg-slate-100 dark:bg-[#21262D] rounded" />)}</div>
      ) : (
        <div className="space-y-1.5 max-h-[400px] overflow-y-auto">
          {locations.sort((a, b) => a.name?.localeCompare(b.name)).map(loc => (
            <div key={loc.location_id} className="flex items-center justify-between px-3 py-2 rounded-lg hover:bg-slate-50 dark:hover:bg-[#0D1117] transition-colors" data-testid={`location-row-${loc.location_id}`}>
              {editingId === loc.location_id ? (
                <>
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    <input
                      data-testid={`edit-name-${loc.location_id}`}
                      type="text" value={editData.name}
                      onChange={e => setEditData({ ...editData, name: e.target.value })}
                      className="px-2 py-1 rounded border border-[#2E75B6]/40 bg-white dark:bg-[#0D1117] text-sm text-slate-900 dark:text-[#E6EDF3] w-32 focus:outline-none focus:ring-1 focus:ring-[#2E75B6]"
                    />
                    <span className="text-[10px] text-slate-400 font-mono">{loc.location_id}</span>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <select
                      data-testid={`edit-status-${loc.location_id}`}
                      value={editData.status}
                      onChange={e => setEditData({ ...editData, status: e.target.value })}
                      className="px-1.5 py-1 rounded border border-slate-200 dark:border-[#30363D] bg-white dark:bg-[#0D1117] text-[10px] text-slate-700 dark:text-[#C9D1D9] focus:outline-none focus:ring-1 focus:ring-[#2E75B6]"
                    >
                      <option value="active">Actief</option>
                      <option value="planned">Gepland</option>
                      <option value="inactive">Inactief</option>
                    </select>
                    <input
                      data-testid={`edit-bays-${loc.location_id}`}
                      type="text" value={editData.bays}
                      onChange={e => setEditData({ ...editData, bays: e.target.value })}
                      placeholder="bays"
                      className="px-2 py-1 rounded border border-slate-200 dark:border-[#30363D] bg-white dark:bg-[#0D1117] text-[10px] text-slate-700 dark:text-[#C9D1D9] w-14 focus:outline-none focus:ring-1 focus:ring-[#2E75B6]"
                    />
                    <button
                      data-testid={`save-location-${loc.location_id}`}
                      onClick={() => saveEdit(loc.location_id)}
                      disabled={editSaving}
                      className="p-1 rounded bg-[#2E75B6] text-white hover:bg-[#27649C] transition-colors disabled:opacity-50"
                    >
                      {editSaving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                    </button>
                    <button onClick={cancelEdit} className="p-1 rounded text-slate-400 hover:text-slate-600 hover:bg-slate-100 dark:hover:bg-[#21262D] transition-colors">
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div className="flex items-center gap-3 min-w-0">
                    <span className="text-sm font-medium text-slate-700 dark:text-[#C9D1D9] truncate">{loc.name}</span>
                    <span className="text-[10px] text-slate-400 font-mono">{loc.location_id}</span>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span className="text-[10px] text-slate-400 capitalize">{loc.bv_id}</span>
                    {loc.bays && <span className="text-[10px] text-slate-400">{loc.bays} bays</span>}
                    <span className={`text-[10px] px-2 py-0.5 rounded-full border capitalize ${STATUS_COLORS[loc.status] || STATUS_COLORS.inactive}`}>
                      {loc.status === 'active' ? 'Actief' : loc.status === 'planned' ? 'Gepland' : 'Inactief'}
                    </span>
                    {isOwner && (
                      <button
                        data-testid={`edit-location-${loc.location_id}`}
                        onClick={() => startEdit(loc)}
                        className="p-1 rounded text-slate-400 hover:text-[#2E75B6] hover:bg-[#2E75B6]/5 transition-colors"
                        title="Bewerken"
                      >
                        <Edit2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function CategoryManagement() {
  const { authAxios } = useAuth();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [form, setForm] = useState({ category_id: '', display_name: '' });
  const [error, setError] = useState('');

  const load = async () => {
    try {
      const api = authAxios();
      const res = await api.get('/categories');
      setCategories(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCreate = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.display_name.trim()) { setError('Vul een naam in'); return; }
    const cid = form.category_id.trim().toLowerCase().replace(/\s+/g, '-') || form.display_name.trim().toLowerCase().replace(/[^a-z0-9]/g, '-').replace(/-+/g, '-');
    setSaving(true);
    try {
      const api = authAxios();
      await api.post('/categories', { category_id: cid, display_name: form.display_name.trim() });
      toast.success('Categorie aangemaakt');
      setShowForm(false);
      setForm({ category_id: '', display_name: '' });
      await load();
    } catch (e) {
      setError(e.response?.data?.detail || 'Fout bij aanmaken');
    } finally { setSaving(false); }
  };

  const handleDelete = async (cid, name) => {
    setDeleting(cid);
    try {
      const api = authAxios();
      await api.delete(`/categories/${encodeURIComponent(cid)}`);
      toast.success(`"${name}" verwijderd`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Kan niet verwijderen');
    } finally { setDeleting(null); }
  };

  return (
    <div className="bg-white dark:bg-[#161B22] rounded-xl border border-slate-200/80 dark:border-[#21262D] p-6" data-testid="category-management">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-heading text-sm font-semibold text-slate-900 dark:text-[#E6EDF3] flex items-center gap-2">
          <Tag className="w-4 h-4" />
          Categorieën beheren
        </h3>
        <button
          data-testid="create-category-button"
          onClick={() => setShowForm(!showForm)}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#2E75B6] text-white text-xs font-medium hover:bg-[#27649C] transition-colors"
        >
          <Plus className="w-3.5 h-3.5" />
          Nieuwe categorie
        </button>
      </div>

      {showForm && (
        <form onSubmit={handleCreate} className="mb-4 p-4 rounded-lg bg-slate-50 dark:bg-[#0D1117] border border-slate-200 dark:border-[#30363D] space-y-3">
          {error && <p className="text-xs text-red-600 bg-red-50 dark:bg-red-500/10 px-3 py-2 rounded-lg">{error}</p>}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-wider text-slate-400 font-medium mb-1 block">Naam *</label>
              <input
                type="text" value={form.display_name} data-testid="category-name-input"
                onChange={e => setForm({ ...form, display_name: e.target.value })}
                placeholder="bijv. Transport & Logistiek" className="w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-[#30363D] bg-white dark:bg-[#161B22] text-slate-900 dark:text-[#E6EDF3] text-sm focus:outline-none focus:ring-2 focus:ring-[#2E75B6]/40"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-slate-400 font-medium mb-1 block">ID (optioneel)</label>
              <input
                type="text" value={form.category_id} data-testid="category-id-input"
                onChange={e => setForm({ ...form, category_id: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') })}
                placeholder="auto-gegenereerd" className="w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-[#30363D] bg-white dark:bg-[#161B22] text-slate-900 dark:text-[#E6EDF3] text-sm focus:outline-none focus:ring-2 focus:ring-[#2E75B6]/40"
              />
            </div>
          </div>
          <div className="flex gap-2 justify-end">
            <button type="button" onClick={() => { setShowForm(false); setError(''); }} className="px-3 py-1.5 rounded-lg border border-slate-200 dark:border-[#30363D] text-xs text-slate-500 hover:bg-slate-100 dark:hover:bg-[#21262D] transition-colors">Annuleren</button>
            <button type="submit" disabled={saving} data-testid="create-category-submit"
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#2E75B6] text-white text-xs font-medium hover:bg-[#27649C] transition-colors disabled:opacity-50">
              {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />} Aanmaken
            </button>
          </div>
        </form>
      )}

      {loading ? (
        <div className="animate-pulse space-y-2">{[1,2,3].map(i => <div key={i} className="h-10 bg-slate-100 dark:bg-[#21262D] rounded" />)}</div>
      ) : (
        <div className="space-y-1.5">
          {categories.map(cat => (
            <div key={cat.category_id} className="flex items-center justify-between px-3 py-2 rounded-lg hover:bg-slate-50 dark:hover:bg-[#0D1117] transition-colors" data-testid={`category-row-${cat.category_id}`}>
              <div className="flex items-center gap-3">
                <span className="text-sm font-medium text-slate-700 dark:text-[#C9D1D9]">{cat.display_name}</span>
                <span className="text-[10px] text-slate-400 font-mono">{cat.category_id}</span>
              </div>
              <button
                onClick={() => handleDelete(cat.category_id, cat.display_name)}
                disabled={deleting === cat.category_id}
                data-testid={`delete-category-${cat.category_id}`}
                className="p-1.5 rounded text-slate-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-500/10 transition-colors disabled:opacity-50"
                title="Verwijderen"
              >
                {deleting === cat.category_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


function AINormalizationSettings() {
  const [autoNormalize, setAutoNormalize] = useState(() => {
    return localStorage.getItem('tw1_ai_normalize_auto') !== 'false';
  });
  const [normalizeOnEmail, setNormalizeOnEmail] = useState(() => {
    return localStorage.getItem('tw1_ai_normalize_email') !== 'false';
  });
  const [normalizeOnUpload, setNormalizeOnUpload] = useState(() => {
    return localStorage.getItem('tw1_ai_normalize_upload') !== 'false';
  });

  const toggle = (key, value, setter) => {
    setter(value);
    localStorage.setItem(key, value.toString());
    toast.success('Voorkeur opgeslagen');
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200/80 p-6" data-testid="ai-normalization-settings">
      <h3 className="font-heading text-sm font-semibold text-slate-900 mb-4 flex items-center gap-2">
        <Sparkles className="w-4 h-4" />
        AI Normalisatie
      </h3>
      <p className="text-xs text-slate-500 mb-4">
        AI Normalisatie analyseert invoer automatisch en stelt opgeschoonde titels, categorieën, compliance-deadlines en tags voor.
      </p>

      <div className="space-y-3">
        {/* Auto-normalize on manual entry */}
        <div className="flex items-center justify-between p-3 rounded-lg bg-slate-50 border border-slate-100">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-[#2E75B6]/10 flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-[#2E75B6]" />
            </div>
            <div>
              <p className="text-sm font-medium text-slate-700">Handmatige invoer</p>
              <p className="text-[11px] text-slate-400">Toon AI Normalisatie-knop bij nieuwe items</p>
            </div>
          </div>
          <button
            data-testid="toggle-ai-normalize-auto"
            onClick={() => toggle('tw1_ai_normalize_auto', !autoNormalize, setAutoNormalize)}
            className={`relative w-10 h-5 rounded-full transition-colors ${autoNormalize ? 'bg-[#2E75B6]' : 'bg-slate-300'}`}
          >
            <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${autoNormalize ? 'translate-x-5' : 'translate-x-0'}`} />
          </button>
        </div>

        {/* Auto-normalize on email ingest */}
        <div className="flex items-center justify-between p-3 rounded-lg bg-slate-50 border border-slate-100">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-[#2E75B6]/10 flex items-center justify-center">
              <Mail className="w-4 h-4 text-[#2E75B6]" />
            </div>
            <div>
              <p className="text-sm font-medium text-slate-700">E-mail ingest</p>
              <p className="text-[11px] text-slate-400">Normaliseer automatisch bij inkomende e-mails</p>
            </div>
          </div>
          <button
            data-testid="toggle-ai-normalize-email"
            onClick={() => toggle('tw1_ai_normalize_email', !normalizeOnEmail, setNormalizeOnEmail)}
            className={`relative w-10 h-5 rounded-full transition-colors ${normalizeOnEmail ? 'bg-[#2E75B6]' : 'bg-slate-300'}`}
          >
            <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${normalizeOnEmail ? 'translate-x-5' : 'translate-x-0'}`} />
          </button>
        </div>

        {/* Auto-normalize on document upload */}
        <div className="flex items-center justify-between p-3 rounded-lg bg-slate-50 border border-slate-100">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-[#2E75B6]/10 flex items-center justify-center">
              <Archive className="w-4 h-4 text-[#2E75B6]" />
            </div>
            <div>
              <p className="text-sm font-medium text-slate-700">Document upload</p>
              <p className="text-[11px] text-slate-400">Normaliseer automatisch bij document-uploads</p>
            </div>
          </div>
          <button
            data-testid="toggle-ai-normalize-upload"
            onClick={() => toggle('tw1_ai_normalize_upload', !normalizeOnUpload, setNormalizeOnUpload)}
            className={`relative w-10 h-5 rounded-full transition-colors ${normalizeOnUpload ? 'bg-[#2E75B6]' : 'bg-slate-300'}`}
          >
            <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${normalizeOnUpload ? 'translate-x-5' : 'translate-x-0'}`} />
          </button>
        </div>
      </div>
    </div>
  );
}


function DefaultObligationOwnerSettings() {
  const { authAxios } = useAuth();
  const [email, setEmail] = useState('');
  const [users, setUsers] = useState([]);
  const [unassignedCount, setUnassignedCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [backfilling, setBackfilling] = useState(false);

  const loadSetting = async (api) => {
    const s = await api.get('/settings/default-obligation-owner');
    setEmail(s.data.email || '');
    setUnassignedCount(s.data.unassigned_count || 0);
  };

  useEffect(() => {
    const load = async () => {
      try {
        const api = authAxios();
        const [, u] = await Promise.all([
          loadSetting(api),
          api.get('/users'),
        ]);
        setUsers((u.data || []).filter((x) => x.status === 'active'));
      } catch (e) {
        console.error('Failed to load default obligation owner:', e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [authAxios]);

  const save = async (newEmail) => {
    setSaving(true);
    try {
      const api = authAxios();
      await api.put(`/settings/default-obligation-owner?email=${encodeURIComponent(newEmail)}`);
      setEmail(newEmail);
      toast.success(newEmail ? 'Standaard eigenaar opgeslagen' : 'Standaard eigenaar gewist');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Fout bij opslaan');
    } finally {
      setSaving(false);
    }
  };

  const runBackfill = async () => {
    setBackfilling(true);
    try {
      const api = authAxios();
      const res = await api.post('/admin/backfill-obligation-owner');
      toast.success(res.data?.message || 'Verplichtingen bijgewerkt');
      await loadSetting(api);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Fout bij toewijzen');
    } finally {
      setBackfilling(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-slate-200/80 p-6">
        <div className="animate-pulse h-20 bg-slate-100 rounded" />
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-slate-200/80 p-6" data-testid="default-obligation-owner-settings">
      <h3 className="font-heading text-sm font-semibold text-slate-900 mb-4 flex items-center gap-2">
        <UserCheck className="w-4 h-4" />
        Standaard eigenaar verplichtingen
      </h3>
      <p className="text-xs text-slate-500 mb-4">
        Automatisch gedetecteerde verplichtingen (uit e-mail of snelle notities) worden aan deze persoon toegewezen
        wanneer er geen andere eigenaar bekend is. Zonder eigenaar verschijnen ze in het dagelijkse overzicht onder
        &quot;Verplichtingen zonder eigenaar&quot;.
      </p>
      <div className="p-3 rounded-lg bg-slate-50 border border-slate-100">
        <label className="block text-xs font-medium text-slate-600 mb-1.5">Standaard eigenaar</label>
        <select
          data-testid="default-obligation-owner-select"
          value={email}
          onChange={(e) => save(e.target.value)}
          disabled={saving}
          className="w-full px-3 py-2 rounded-lg border border-slate-200 bg-white text-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-[#2E75B6]/40 focus:border-[#2E75B6] transition-colors"
        >
          <option value="">Geen — toon als &quot;zonder eigenaar&quot;</option>
          {users.map((u) => (
            <option key={u.email} value={u.email}>{(u.name || u.email)} ({u.email})</option>
          ))}
        </select>
      </div>

      {/* One-off legacy cleanup */}
      <div className="mt-4 pt-4 border-t border-slate-100 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <p className="text-xs text-slate-500 flex-1">
          Wijs bestaande verplichtingen zonder geldige eigenaar in één keer toe aan de standaard eigenaar.
          {!email && <span className="text-amber-600"> Stel eerst een standaard eigenaar in.</span>}
        </p>
        <button
          type="button"
          data-testid="backfill-obligation-owner-btn"
          onClick={runBackfill}
          disabled={backfilling || !email || unassignedCount === 0}
          className="inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-lg bg-[#2E75B6] text-white text-sm font-medium hover:bg-[#27649C] active:scale-[0.98] transition-colors disabled:opacity-50 whitespace-nowrap"
        >
          {backfilling
            ? 'Bezig met toewijzen...'
            : `Bestaande verplichtingen zonder eigenaar toewijzen (${unassignedCount})`}
        </button>
      </div>
    </div>
  );
}


function AutoApproveSettings() {
  const { authAxios } = useAuth();
  const [enabled, setEnabled] = useState(false);
  const [threshold, setThreshold] = useState(0.8);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const api = authAxios();
        const res = await api.get('/settings/auto-approve');
        setEnabled(res.data.enabled);
        setThreshold(res.data.threshold);
      } catch (e) {
        console.error('Failed to load auto-approve settings:', e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [authAxios]);

  const save = async (newEnabled, newThreshold) => {
    setSaving(true);
    try {
      const api = authAxios();
      await api.put(`/settings/auto-approve?enabled=${newEnabled}&threshold=${newThreshold}`);
      toast.success('Instelling opgeslagen');
    } catch (e) {
      toast.error('Fout bij opslaan');
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = () => {
    const next = !enabled;
    setEnabled(next);
    save(next, threshold);
  };

  const handleThresholdChange = (e) => {
    const val = parseFloat(e.target.value);
    setThreshold(val);
  };

  const handleThresholdCommit = () => {
    save(enabled, threshold);
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-slate-200/80 p-6">
        <div className="animate-pulse h-20 bg-slate-100 rounded" />
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-slate-200/80 p-6" data-testid="auto-approve-settings">
      <h3 className="font-heading text-sm font-semibold text-slate-900 mb-4 flex items-center gap-2">
        <CheckCircle2 className="w-4 h-4" />
        Auto-approve bij hoge AI confidence
      </h3>
      <p className="text-xs text-slate-500 mb-4">
        Wanneer ingeschakeld, worden items uit e-mail en document-ingest automatisch goedgekeurd als de AI-confidence boven de drempel ligt.
      </p>

      <div className="space-y-4">
        {/* Toggle */}
        <div className="flex items-center justify-between p-3 rounded-lg bg-slate-50 border border-slate-100">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-green-500/10 flex items-center justify-center">
              <CheckCircle2 className="w-4 h-4 text-green-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-slate-700">Auto-approve inschakelen</p>
              <p className="text-[11px] text-slate-400">Items met hoge confidence slaan "Te beoordelen" over</p>
            </div>
          </div>
          <button
            data-testid="toggle-auto-approve"
            onClick={handleToggle}
            disabled={saving}
            className={`relative w-10 h-5 rounded-full transition-colors ${enabled ? 'bg-green-500' : 'bg-slate-300'}`}
          >
            <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${enabled ? 'translate-x-5' : 'translate-x-0'}`} />
          </button>
        </div>

        {/* Slider */}
        {enabled && (
          <div className="p-3 rounded-lg bg-slate-50 border border-slate-100" data-testid="auto-approve-slider-section">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-medium text-slate-600">Confidence drempel</p>
              <span className="text-sm font-bold text-slate-800">{Math.round(threshold * 100)}%</span>
            </div>
            <input
              type="range"
              data-testid="auto-approve-slider"
              min="0.7"
              max="0.95"
              step="0.05"
              value={threshold}
              onChange={handleThresholdChange}
              onMouseUp={handleThresholdCommit}
              onTouchEnd={handleThresholdCommit}
              className="w-full h-1.5 bg-slate-200 rounded-full appearance-none cursor-pointer accent-green-500"
            />
            <div className="flex justify-between text-[10px] text-slate-400 mt-1">
              <span>70%</span>
              <span>95%</span>
            </div>
            <p className="text-[10px] text-slate-400 mt-2">
              Hogere drempel = minder automatisch goedgekeurd, maar hogere kwaliteit.
              {threshold <= 0.7 && ' Bij 70% worden alle items met medium+ confidence goedgekeurd.'}
              {threshold > 0.85 && ' Bij deze drempel worden alleen items met hoge confidence goedgekeurd.'}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
